import {
  ApplicationIcon,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Icon,
  Loader,
  Typography,
} from "@saviynt/design-system";
import { FormattedMessage } from "react-intl";
import { isAllowedIntegrationType } from "../../constants/integrationTypes";

export interface IntegrationType {
  id: number;
  typeName: string;
  displayName: string;
  description: string;
  iconUrl: string;
  isEnabled: boolean;
  isPrivateApp: boolean;
  encryptionConfig: string;
  value: string;
  label: string;
}

interface IntegrationTypeSelectorProps {
  integrationTypes: IntegrationType[];
  isLoading: boolean;
  error: Error | null;
  onSelect: (typeName: string) => void;
}

export function IntegrationTypeSelector({
  integrationTypes,
  isLoading,
  error,
  onSelect,
}: IntegrationTypeSelectorProps) {
  return (
    <div className="flex items-center justify-center min-h-full p-6">
      {isLoading && (
        <Loader kind="loop" format="main" color="brand" size="large" />
      )}

      {error && (
        <div className="text-center text-red-500 p-4">
          <FormattedMessage
            id="+Jl9a3"
            defaultMessage="Failed to load integration types"
            description="Error message when integration types fail to load"
          />
        </div>
      )}

      {integrationTypes && (
        <div className="flex flex-wrap justify-center gap-4 max-w-6xl w-full">
          {integrationTypes
            .filter(
              (type) =>
                type.isEnabled && isAllowedIntegrationType(type.typeName),
            )
            .map((type) => (
              <Card
                key={type.id}
                className="w-1/3 bg-[var(--color-background-secondary-subtlest)] shadow-[var(--box-shadow-pretoken-container-default)]"
              >
                <CardHeader className="flex flex-row p-4 gap-4 items-center bg-[var(--color-background-secondary-subtlest)]">
                  <div className="flex items-center gap-4">
                    <ApplicationIcon
                      logo={
                        type.typeName === "GOOGLE_VERTEX_DISCOVERY"
                          ? "GCP"
                          : type.typeName
                      }
                      size="large"
                    />
                    <div className="flex-1 min-w-0">
                      <Typography kind="h3" className="mb-1">
                        {type.displayName}
                      </Typography>
                    </div>
                  </div>
                </CardHeader>
                <CardBody className="p-4 bg-[var(--color-background-secondary-subtlest)]">
                  <Typography
                    kind="body2"
                    className="text-[var(--color-foreground-neutral-medium)]"
                  >
                    {type.description}
                  </Typography>
                </CardBody>
                <div
                  className="border-t"
                  style={{
                    borderTopColor:
                      "var(--color-border-secondary-subtlest, #293950)",
                  }}
                >
                  <CardFooter className="p-2 bg-[var(--color-background-secondary-subtlest)]">
                    <Button
                      kind="ghost"
                      size="medium"
                      className="w-full justify-center"
                      onClick={() => onSelect(type.typeName)}
                      rightIcon={<Icon kind="ArrowRight" />}
                    >
                      <FormattedMessage
                        id="mmXmAV"
                        defaultMessage="Integrate"
                        description="Button to integrate with service"
                      />
                    </Button>
                  </CardFooter>
                </div>
              </Card>
            ))}
        </div>
      )}
    </div>
  );
}
